"use client";

import { useMemo, useState } from "react";
import { Download, TrendingUp, ChevronDown, Sparkles } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PLAY_STORE_URL } from "@/app/lib/constants";
import MoneyInput from "@/app/components/calculadora/MoneyInput";

/** Formatea pesos AR sin decimales: 1234567 -> "$1.234.567". */
const fmt = (n: number) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

/**
 * Abrevia montos grandes para que se lean (un número de 13 dígitos parece un
 * bug): $9,7 millones, $1,2 mil millones, $4,79 billones. Abajo de 1 millón
 * muestra el monto completo en pesos.
 */
const fmtBig = (n: number): string => {
  const abs = Math.abs(n);
  const cap = (v: number, unidad: string) =>
    `$${v.toLocaleString("es-AR", { maximumFractionDigits: 2 })} ${unidad}`;
  if (abs >= 1e12) return cap(n / 1e12, "billones");
  if (abs >= 1e9) return cap(n / 1e9, "mil millones");
  if (abs >= 1e6) return cap(n / 1e6, "millones");
  return fmt(n);
};

/** Multiplicador legible: 2,3 veces / ×494.012. */
const fmtMult = (x: number) =>
  x >= 100
    ? `${Math.round(x).toLocaleString("es-AR")} veces`
    : `${x.toFixed(1).replace(".", ",")} veces`;

interface SliderRowProps {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  prefix?: string;
  suffix?: string;
  /** Monto en pesos: muestra separadores de miles mientras se escribe. */
  money?: boolean;
  onChange: (v: number) => void;
}

function SliderRow({
  label,
  hint,
  value,
  min,
  max,
  step,
  prefix,
  suffix,
  money = false,
  onChange,
}: SliderRowProps) {
  const inputClass =
    "w-28 sm:w-32 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-right text-white font-semibold text-sm focus:border-primary focus:outline-none";
  return (
    <div className="mb-6">
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <label className="text-sm sm:text-base text-white/80 font-medium">
          {label}
        </label>
        <div className="flex items-center gap-1 shrink-0">
          {prefix && <span className="text-white/40 text-sm">{prefix}</span>}
          {money ? (
            <MoneyInput
              value={value}
              onChange={onChange}
              aria-label={label}
              className={inputClass}
            />
          ) : (
            <input
              type="number"
              value={value}
              min={min}
              max={max}
              step={step}
              aria-label={label}
              onChange={(e) => onChange(Number(e.target.value) || 0)}
              className={inputClass}
            />
          )}
          {suffix && <span className="text-white/40 text-sm">{suffix}</span>}
        </div>
      </div>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full cursor-pointer"
        style={{ accentColor: "#00D9C4" }}
      />
      {hint && <p className="text-white/35 text-xs mt-1.5">{hint}</p>}
    </div>
  );
}

interface TooltipPayloadItem {
  dataKey: string;
  value: number;
}
function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: number;
}) {
  if (!active || !payload?.length) return null;
  const con = payload.find((p) => p.dataKey === "con")?.value ?? 0;
  const sin = payload.find((p) => p.dataKey === "sin")?.value ?? 0;
  return (
    <div className="rounded-xl bg-[#0F1822] border border-white/15 px-3 py-2 text-xs shadow-2xl">
      <p className="text-white/50 mb-1">{label === 0 ? "Hoy" : `Año ${label}`}</p>
      <p className="text-primary font-bold text-sm">{fmtBig(con)}</p>
      <p className="text-white/45 mt-0.5">Si solo guardaras: {fmtBig(sin)}</p>
    </div>
  );
}

export default function CompoundInterestCalculator() {
  const [capital, setCapital] = useState(100_000);
  const [aporte, setAporte] = useState(20_000);
  const [tasa, setTasa] = useState(40); // % anual
  const [anios, setAnios] = useState(10);
  const [showFormula, setShowFormula] = useState(false);

  const { chartData, totalFinal, totalAportado, intereses } = useMemo(() => {
    const meses = Math.max(1, Math.round(anios * 12));
    const rMensual = tasa / 100 / 12;
    let balance = capital;
    let aportado = capital;
    const con: number[] = [capital];
    const sin: number[] = [capital];
    for (let m = 1; m <= meses; m++) {
      balance = balance * (1 + rMensual) + aporte;
      aportado += aporte;
      con.push(balance);
      sin.push(aportado);
    }
    const chartData = Array.from({ length: anios + 1 }, (_, yy) => {
      const idx = Math.min(yy * 12, con.length - 1);
      return { year: yy, con: con[idx], sin: sin[idx] };
    });
    return {
      chartData,
      totalFinal: balance,
      totalAportado: aportado,
      intereses: balance - aportado,
    };
  }, [capital, aporte, tasa, anios]);

  const multiplicador = totalAportado > 0 ? totalFinal / totalAportado : 0;

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 sm:p-8 backdrop-blur-sm">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        {/* ---------------- Controles ---------------- */}
        <div>
          <SliderRow
            label="¿Cuánto ponés hoy?"
            prefix="$"
            money
            value={capital}
            min={0}
            max={10_000_000}
            step={10_000}
            onChange={setCapital}
          />
          <SliderRow
            label="¿Cuánto sumás por mes?"
            hint="Lo que vas agregando todos los meses. Podés dejarlo en 0."
            prefix="$"
            money
            value={aporte}
            min={0}
            max={1_000_000}
            step={5_000}
            onChange={setAporte}
          />
          <SliderRow
            label="¿Qué interés te dan por año?"
            hint="El porcentaje anual que rinde tu plata."
            suffix="%"
            value={tasa}
            min={0}
            max={150}
            step={1}
            onChange={setTasa}
          />
          <SliderRow
            label="¿Cuántos años la dejás crecer?"
            suffix="años"
            value={anios}
            min={1}
            max={40}
            step={1}
            onChange={setAnios}
          />
        </div>

        {/* ---------------- Resultado ---------------- */}
        <div className="flex flex-col">
          <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 p-5 mb-4">
            <p className="text-white/60 text-sm mb-1">
              En {anios} {anios === 1 ? "año" : "años"} vas a tener
            </p>
            <p className="text-3xl sm:text-4xl font-black text-primary leading-none break-words">
              {fmtBig(totalFinal)}
            </p>
            {multiplicador > 1.05 && (
              <p className="flex items-center gap-1.5 text-white/50 text-xs mt-2">
                <Sparkles
                  className="w-3.5 h-3.5 text-primary shrink-0"
                  aria-hidden="true"
                />
                <span>Tu plata se multiplicó {fmtMult(multiplicador)}</span>
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="rounded-xl bg-white/5 border border-white/10 p-3">
              <p className="text-white/50 text-xs mb-1">Lo que pusiste vos</p>
              <p className="text-lg font-bold text-white break-words">
                {fmtBig(totalAportado)}
              </p>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-3">
              <p className="text-white/50 text-xs mb-1 flex items-center gap-1">
                <TrendingUp size={12} className="text-primary" />
                Lo que se hizo solo
              </p>
              <p className="text-lg font-bold text-primary break-words">
                {fmtBig(intereses)}
              </p>
            </div>
          </div>

          {/* Gráfico */}
          <div className="mt-auto">
            <div className="flex items-center gap-4 mb-2 text-xs">
              <span className="flex items-center gap-1.5 text-white/70">
                <span className="inline-block w-3 h-2 rounded-sm bg-primary" />
                Con interés compuesto
              </span>
              <span className="flex items-center gap-1.5 text-white/45">
                <span className="inline-block w-3 border-t-2 border-dashed border-white/40" />
                Si solo guardaras
              </span>
            </div>
            <div className="h-44 sm:h-52 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={chartData}
                  margin={{ top: 6, right: 8, bottom: 0, left: 8 }}
                >
                  <defs>
                    <linearGradient id="gCon" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00D9C4" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#00D9C4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.06)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="year"
                    tickFormatter={(y: number) => (y === 0 ? "hoy" : `${y}a`)}
                    stroke="rgba(255,255,255,0.3)"
                    tick={{ fontSize: 11, fill: "rgba(255,255,255,0.4)" }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={20}
                  />
                  <YAxis hide domain={[0, "dataMax"]} />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ stroke: "rgba(255,255,255,0.15)" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="sin"
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    fill="none"
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="con"
                    stroke="#00D9C4"
                    strokeWidth={2.5}
                    fill="url(#gCon)"
                    dot={false}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* ---------------- Explicación ---------------- */}
      <div className="mt-8 pt-6 border-t border-white/10">
        <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-2xl">
          <span className="text-white font-semibold">¿Por qué crece tanto?</span>{" "}
          Ponés una plata, la dejás un tiempo, y los intereses generan más
          intereses. La línea teal se despega de la punteada justamente por eso:
          esa distancia es plata que se hizo sola. Cuanto más tiempo la dejes,
          más se separan.
        </p>

        <button
          onClick={() => setShowFormula((v) => !v)}
          className="mt-4 inline-flex items-center gap-1 text-primary text-sm hover:underline"
        >
          {showFormula ? "Ocultar la fórmula" : "Ver la fórmula de verdad"}
          <ChevronDown
            size={15}
            className={`transition-transform ${showFormula ? "rotate-180" : ""}`}
          />
        </button>
        {showFormula && (
          <div className="mt-3 rounded-xl bg-white/5 border border-white/10 p-5 text-sm text-white/65 max-w-2xl leading-relaxed space-y-4">
            <div>
              <p className="font-mono text-white/90 text-base mb-3 tracking-tight">
                Capital final = C₀ × (1 + i)<sup>t</sup>
              </p>
              <ul className="space-y-1.5">
                <li>
                  <span className="text-primary font-mono">C₀</span> es la plata
                  que ponés al principio.
                </li>
                <li>
                  <span className="text-primary font-mono">i</span> es el interés
                  por período, en decimal (un 40% anual es 0,40).
                </li>
                <li>
                  <span className="text-primary font-mono">t</span> es la
                  cantidad de períodos que la dejás.
                </li>
              </ul>
            </div>

            <div className="pt-1">
              <p className="text-white/90 font-semibold mb-1">
                Lo que lo hace mágico
              </p>
              <p>
                Con interés <span className="text-white">simple</span> ganás
                siempre sobre la plata inicial: el mismo monto cada año. Con
                interés <span className="text-primary">compuesto</span>, lo que
                ganás se suma a tu capital y empieza a generar lo suyo. Los
                intereses generan intereses, y por eso la curva se dispara
                cuanto más tiempo pasa.
              </p>
            </div>

            <p className="text-white/45 text-xs">
              Acá además sumamos tus aportes mes a mes y capitalizamos todos los
              meses. No necesitás saber nada de esto para usarla: movés los
              controles y ves el resultado.
            </p>
          </div>
        )}
      </div>

      {/* ---------------- CTA ---------------- */}
      <div className="mt-8 rounded-2xl bg-white/[0.04] border border-white/10 p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-white/75 text-sm sm:text-base text-center sm:text-left">
          En <span className="text-primary font-semibold">Finbo</span> ponés tu
          meta y la app te hace estas cuentas sola, con tu plata real.
        </p>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary shrink-0"
        >
          <Download size={18} />
          Descargar gratis
        </a>
      </div>
    </div>
  );
}
